import React, { useState } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { ShieldCheck, Check, ChevronDown, ChevronUp, Star, Rocket, X, Gift, Zap } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
// Custom Icons
function GiftIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-279">
      <polyline points="20 12 20 22 4 22 4 12" data-id="element-280"></polyline>
      <rect x="2" y="7" width="20" height="5" data-id="element-281"></rect>
      <line x1="12" y1="22" x2="12" y2="7" data-id="element-282"></line>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" data-id="element-283"></path>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" data-id="element-284"></path>
    </svg>;
}
function CheckIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-285">
      <polyline points="20 6 9 17 4 12" data-id="element-286"></polyline>
    </svg>;
}
function SparkleIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" data-id="element-287">
      <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" data-id="element-288" />
    </svg>;
}
// Base prices in USD
const PRICES = {
  product: 6.39,
  productOriginal: 97,
  bonuses: 47,
  total: 144,
  orderBump: 6
};
// Shopify checkout URLs based on product combinations
const CHECKOUT_URLS = {
  mainOnly: 'https://shop.hydraglosslips.com/cart/47864410407160:1',
  mainPlusBump1: 'https://shop.hydraglosslips.com/cart/47952138174712:1',
  mainPlusBump2: 'https://shop.hydraglosslips.com/cart/47952204103928:1',
  mainPlusBoth: 'https://shop.hydraglosslips.com/cart/47952185852152:1'
};
interface PricingProps {
  disableUpsell?: boolean;
  upsellAcceptUrl?: string;
}
export function Pricing({
  disableUpsell = false,
  upsellAcceptUrl
}: PricingProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [orderBump1, setOrderBump1] = useState(false);
  const [orderBump2, setOrderBump2] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const {
    formatPrice,
    currency,
    isLoading
  } = useCurrency();
  // Calculate total price dynamically
  const totalPrice = PRICES.product + (orderBump1 ? PRICES.orderBump : 0) + (orderBump2 ? PRICES.orderBump : 0);
  // Generate dynamic checkout URL based on selected products
  const getCheckoutUrl = () => {
    if (orderBump1 && orderBump2) {
      // Main product + both order bumps
      return CHECKOUT_URLS.mainPlusBoth;
    } else if (orderBump1) {
      // Main product + order bump 1 only
      return CHECKOUT_URLS.mainPlusBump1;
    } else if (orderBump2) {
      // Main product + order bump 2 only
      return CHECKOUT_URLS.mainPlusBump2;
    } else {
      // Main product only
      return CHECKOUT_URLS.mainOnly;
    }
  };
  const handleCheckout = () => {
    // If no order bumps selected and upsell is enabled, show upsell popup
    if (!orderBump1 && !orderBump2 && !disableUpsell) {
      setShowUpsell(true);
      return;
    }
    // Otherwise proceed to normal checkout
    window.location.href = getCheckoutUrl();
  };
  const handleAcceptUpsell = () => {
    window.location.href = upsellAcceptUrl || 'https://shop.hydraglosslips.com/cart/47997641851128:1';
  };
  const handleDeclineUpsell = () => {
    window.location.href = 'https://shop.hydraglosslips.com/cart/47864410407160:1';
  };
  const bonuses = [{
    title: 'Pack Portadas Premium',
    desc: '10 Portadas Editables en Gamma',
    image: 'https://cdn.magicpatterns.com/uploads/oPjbSiyoEGgPdKx1TT2bff/bono_1.png',
    subtitle: '10 Portadas Editables en Gamma (desde el celular)',
    hook: 'Tu PDF puede ser simple... pero tiene que verse pagable.',
    description: 'Este bono está diseñado para que tu producto no se vea "casero" ni improvisado, sino limpio, moderno, premium.',
    includes: ['10 estilos de portada listos para replicar en Gamma', 'Fórmulas de títulos que suenan a producto real (sin hype)', 'Checklist "No se ve amateur" antes de exportar'],
    result: 'Tu PDF se ve como algo que la gente compra, no como algo que regalan.'
  }, {
    title: 'Oferta Express (15 Min)',
    desc: 'Tu Precio + Tu Paquete Sin Dudar',
    image: 'https://cdn.magicpatterns.com/uploads/1idxmYwwtZbiXSe3Yuj4dx/bono_2.png',
    subtitle: 'Tu Precio + Tu Paquete Sin Dudar',
    hook: 'La mayoría no falla por falta de talento. Falla por esto: no cierra la oferta... y por eso le da pena cobrar.',
    description: 'Con este bono sales con tu oferta lista y un precio que puedes decir sin temblar.',
    includes: ['Plantilla "Mi oferta en 6 líneas" (rellenable)', 'Tabla de precio (Starter / Standard / Premium)', '12 frases para decir tu precio sin justificarte'],
    result: 'Publicas con seguridad, sin rebajar, sin pedir permiso.'
  }, {
    title: 'Entrega Pro (Celular)',
    desc: 'Envío Premium + Testimonios',
    image: 'https://cdn.magicpatterns.com/uploads/1SJ87rmaR1AxATNiHAPpKu/bono_3.png',
    subtitle: 'Envío Premium + Testimonios',
    hook: 'Una entrega desordenada mata la confianza. Una entrega limpia te convierte en marca.',
    description: 'Este bono te deja un sistema simple para entregar tu PDF como profesional y encima conseguir testimonios.',
    includes: ['Checklist de entrega perfecta (paso a paso)', 'Mensajes listos (WhatsApp + Instagram)', 'Follow-up 24h + guion para pedir testimonio sin incomodar'],
    result: 'Cada venta se siente pro, y las siguientes se vuelven más fáciles.'
  }];
  return <Section id="pricing" className="bg-white" data-id="element-289">
      {/* Upsell Modal */}
      {showUpsell && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-290">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowUpsell(false)} data-id="element-291" />
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in" data-id="element-292">
            {/* Close button */}
            <button onClick={() => setShowUpsell(false)} className="absolute top-3 right-3 z-10 p-1.5 bg-white/80 hover:bg-gray-100 rounded-full transition-colors" data-id="element-293">
              <X className="w-4 h-4 text-gray-500" data-id="element-294" />
            </button>

            {/* Header Banner */}
            <div className="bg-gradient-to-r from-brand-pink to-rose-400 px-6 py-4 text-center" data-id="element-295">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-2" data-id="element-296">
                <Zap className="w-3.5 h-3.5 text-white fill-white" data-id="element-297" />
                <span className="text-white text-xs font-bold uppercase tracking-wider" data-id="element-298">
                  Oferta Única — Solo Ahora
                </span>
              </div>
              <h3 className="text-white text-2xl font-extrabold leading-tight" data-id="element-299">
                ¡ESPERA! 🎁
              </h3>
              <p className="text-white/90 text-sm font-medium mt-1" data-id="element-300">
                Tenemos algo especial para ti
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-5" data-id="element-301">
              {/* 30% OFF Badge */}
              <div className="flex justify-center mb-4" data-id="element-302">
                <div className="bg-red-500 text-white font-extrabold text-lg px-5 py-2 rounded-full shadow-lg animate-flash-badge" data-id="element-303">
                  30% OFF — Paquete Completo
                </div>
              </div>

              <p className="text-gray-800 text-center text-base font-semibold leading-snug mb-3" data-id="element-304">
                Te recomendamos este paquete para que no te falte nada y tengas
                todo lo necesario para poder lanzar tu producto.
              </p>

              <p className="text-gray-500 text-center text-sm mb-5" data-id="element-305">
                Incluye los 2 extras que te ayudarán a publicar, cobrar y
                entregar como profesional desde el día 1.
              </p>

              {/* What's included */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5" data-id="element-306">
                <p className="font-bold text-emerald-800 text-sm mb-3 flex items-center gap-1.5" data-id="element-307">
                  <Gift className="w-4 h-4" data-id="element-308" />
                  Tu paquete completo incluye:
                </p>
                <ul className="space-y-2" data-id="element-309">
                  {['Mamá Digital 2.0 (Guía completa)', '3 Bonos Premium GRATIS', 'Kit Copy-Paste (textos listos para publicar)', 'Cobro + Entrega Pro (cobra como marca)'].map((item, i) => <li key={i} className="flex items-start text-sm text-emerald-900" data-id="element-310">
                      <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-311" />
                      <span data-id="element-312">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Price comparison */}
              <div className="text-center mb-5" data-id="element-313">
                <span className="text-gray-400 line-through text-lg" data-id="element-314">
                  $18.39
                </span>
                <span className="text-3xl font-extrabold text-brand-pink ml-2" data-id="element-315">
                  $12.87
                </span>
                <p className="text-gray-500 text-xs mt-1" data-id="element-316">
                  Precio especial solo en esta pantalla
                </p>
              </div>

              {/* Accept Button */}
              <button onClick={handleAcceptUpsell} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base py-4 px-6 rounded-full transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 mb-3 flex items-center justify-center gap-2" data-id="element-317">
                <Rocket className="w-5 h-5" data-id="element-318" />
                ¡SÍ! Quiero el Paquete Completo
              </button>

              {/* Decline Button */}
              <button onClick={handleDeclineUpsell} className="w-full text-gray-400 hover:text-gray-500 text-xs py-2 transition-colors underline underline-offset-2" data-id="element-319">
                No gracias, continuar sin los extras →
              </button>
            </div>
          </div>
        </div>}

      <div className="max-w-4xl mx-auto" data-id="element-320">
        <div className="bg-white rounded-3xl shadow-2xl border-2 border-brand-pink overflow-hidden" data-id="element-321">
          <div className="bg-brand-pink p-4 text-center" data-id="element-322">
            <span className="text-brand-dark font-bold text-sm uppercase tracking-wider" data-id="element-323">
              Oferta Especial de Lanzamiento
            </span>
          </div>

          <div className="p-8 md:p-10" data-id="element-324">
            <div className="text-center mb-8" data-id="element-325">
              <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-326">
                Mamá Digital 2.0
              </h2>
              <p className="text-gray-500" data-id="element-327">
                Tu primer producto digital puede ser más simple de lo que
                imaginas
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-328">
              {/* Left Column - What's Included */}
              <div data-id="element-329">
                <div className="bg-gray-50 p-6 rounded-xl mb-6" data-id="element-330">
                  <p className="font-bold text-gray-900 mb-4" data-id="element-331">
                    Estás comprando:
                  </p>
                  <ul className="space-y-3" data-id="element-332">
                    {['Un método que te dice qué crear', 'Cómo estructurarlo para que se vea profesional', 'Cómo terminarlo rápido sin quemarte', 'Cómo venderlo sin sentirte incómoda'].map((item, i) => <li key={i} className="flex items-start text-gray-700 text-sm" data-id="element-333">
                        <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-334" />
                        <span data-id="element-335">{item}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Bonuses Section */}
                <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl" data-id="element-336">
                  <div className="flex items-center gap-2 mb-4" data-id="element-337">
                    <GiftIcon className="w-5 h-5 text-amber-600" data-id="element-338" />
                    <p className="font-bold text-gray-900" data-id="element-339">
                      + 3 Bonos GRATIS{' '}
                      <span className="text-amber-600" data-id="element-340">
                        (Valor {formatPrice(PRICES.bonuses)})
                      </span>
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-341">
                    {bonuses.map((bonus, i) => <div key={i} className="flex items-center gap-3" data-id="element-342">
                        <img src={bonus.image} alt={bonus.title} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" data-id="element-343" />
                        <div data-id="element-344">
                          <p className="font-semibold text-gray-900 text-sm" data-id="element-345">
                            {bonus.title}
                          </p>
                          <p className="text-gray-500 text-xs" data-id="element-346">{bonus.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* Read More Button */}
                  <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 w-full flex items-center justify-center gap-2 text-amber-700 hover:text-amber-800 font-medium text-sm py-2 border-t border-amber-200 transition-colors" data-id="element-347">
                    {showBonusDetails ? <>
                        <span data-id="element-348">Ver menos</span>
                        <ChevronUp className="w-4 h-4" data-id="element-349" />
                      </> : <>
                        <span data-id="element-350">Leer más</span>
                        <ChevronDown className="w-4 h-4" data-id="element-351" />
                      </>}
                  </button>

                  {/* Expanded Bonus Details */}
                  {showBonusDetails && <div className="mt-4 pt-4 border-t border-amber-200 space-y-6" data-id="element-352">
                      {bonuses.map((bonus, index) => <div key={index} className="bg-white rounded-xl p-4 border border-amber-100" data-id="element-353">
                          <div className="flex items-start gap-3 mb-3" data-id="element-354">
                            <img src={bonus.image} alt={bonus.title} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" data-id="element-355" />
                            <div data-id="element-356">
                              <span className="bg-brand-pink text-brand-dark text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-357">
                                BONO #{index + 1}
                              </span>
                              <h4 className="text-lg font-bold text-gray-900 mt-1" data-id="element-358">
                                {bonus.title}
                              </h4>
                              <p className="text-brand-pink font-medium text-sm" data-id="element-359">
                                {bonus.subtitle}
                              </p>
                            </div>
                          </div>

                          <p className="text-gray-600 italic text-sm mb-2" data-id="element-360">
                            {bonus.hook}
                          </p>

                          <p className="text-gray-500 text-sm mb-3" data-id="element-361">
                            {bonus.description}
                          </p>

                          <div className="mb-3" data-id="element-362">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold" data-id="element-363">
                              Incluye:
                            </p>
                            <ul className="space-y-1" data-id="element-364">
                              {bonus.includes.map((item, i) => <li key={i} className="flex items-start text-gray-700" data-id="element-365">
                                  <CheckIcon className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-366" />
                                  <span className="text-xs" data-id="element-367">{item}</span>
                                </li>)}
                            </ul>
                          </div>

                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-id="element-368">
                            <div className="flex items-start gap-2" data-id="element-369">
                              <SparkleIcon className="w-3 h-3 text-emerald-500 mt-1 flex-shrink-0" data-id="element-370" />
                              <p className="text-emerald-700 font-medium text-xs" data-id="element-371">
                                <span className="font-bold" data-id="element-372">Resultado:</span>{' '}
                                {bonus.result}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </div>
              </div>

              {/* Right Column - Price & CTA */}
              <div className="flex flex-col justify-center" data-id="element-373">
                <div className="text-center mb-6" data-id="element-374">
                  <div className="flex items-center justify-center mb-2" data-id="element-375">
                    <span className="text-gray-400 text-xl line-through mr-3" data-id="element-376">
                      {formatPrice(PRICES.productOriginal)}
                    </span>
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded" data-id="element-377">
                      93% OFF
                    </span>
                  </div>
                  <div className="flex items-baseline justify-center" data-id="element-378">
                    <span className={`font-extrabold text-brand-pink transition-all duration-300 ${isLoading ? 'opacity-50' : ''}`} style={{
                    fontSize: currency.rate > 100 ? '3rem' : '3.75rem'
                  }} data-id="element-379">
                      {formatPrice(totalPrice)}
                    </span>
                    <span className="text-gray-500 text-lg ml-2" data-id="element-380">
                      {currency.code}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mt-2" data-id="element-381">
                    Pago único • Acceso inmediato
                  </p>
                  {currency.code !== 'USD' && <p className="text-gray-400 text-xs mt-1" data-id="element-382">
                      (≈ ${totalPrice.toFixed(2)} USD)
                    </p>}
                </div>

                {/* Updated Top Button - Now green like bottom button */}
                <button onClick={handleCheckout} className="w-full mb-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg py-5 px-6 rounded-full transition-all duration-200 transform hover:-translate-y-1 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center justify-center gap-2" data-id="element-383">
                  <Rocket className="w-5 h-5" data-id="element-384" />
                  ¡Lanza Tu Negocio En 7 Días! • {formatPrice(totalPrice)}
                </button>

                {/* Order Bump #1 */}
                <div className={`mb-4 border-2 rounded-xl overflow-hidden cursor-pointer transition-all animate-subtle-flash ${orderBump1 ? 'border-brand-pink bg-pink-50' : 'border-brand-pink bg-white hover:bg-pink-50/50'}`} onClick={() => setOrderBump1(!orderBump1)} data-id="element-385">
                  {/* Header Badge */}
                  <div className="bg-brand-pink px-4 py-1.5 flex items-center justify-center gap-1.5" data-id="element-386">
                    <Star className="w-3.5 h-3.5 text-brand-dark fill-brand-dark" data-id="element-387" />
                    <span className="text-brand-dark font-bold text-xs uppercase tracking-wide" data-id="element-388">
                      Altamente Recomendado
                    </span>
                    <Star className="w-3.5 h-3.5 text-brand-dark fill-brand-dark" data-id="element-389" />
                  </div>

                  <div className="p-4" data-id="element-390">
                    <div className="flex items-start gap-3" data-id="element-391">
                      <div className="flex-shrink-0 mt-1" data-id="element-392">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${orderBump1 ? 'bg-brand-pink border-brand-pink' : 'border-gray-300'}`} data-id="element-393">
                          {orderBump1 && <Check className="w-3 h-3 text-white" data-id="element-394" />}
                        </div>
                      </div>
                      <div className="flex-1" data-id="element-395">
                        <div className="flex items-center gap-2 mb-1" data-id="element-396">
                          <img src="https://cdn.magicpatterns.com/uploads/aixDogy7TK1DbqokeLyfPy/order_bump_1.png" alt="Kit Copy-Paste" className="w-12 h-12 rounded-lg object-cover" data-id="element-397" />
                          <div data-id="element-398">
                            <h4 className="font-bold text-gray-900 text-sm" data-id="element-399">
                              Publica Hoy (Sin Pensar Qué Decir)
                            </h4>
                            <p className="text-brand-pink font-bold text-sm" data-id="element-400">
                              + {formatPrice(PRICES.orderBump)}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-600 text-xs leading-relaxed" data-id="element-401">
                          Agrega el Kit Copy‑Paste y recibe textos listos para
                          Historias de IG + Estados de WhatsApp, con un tono
                          limpio y seguro. Copia, pega y publica para que te
                          escriban "info" y empiecen las conversaciones sin
                          pena.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Bump #2 */}
                <div className={`mb-4 border-2 rounded-xl overflow-hidden cursor-pointer transition-all animate-subtle-flash ${orderBump2 ? 'border-brand-pink bg-pink-50' : 'border-brand-pink bg-white hover:bg-pink-50/50'}`} onClick={() => setOrderBump2(!orderBump2)} data-id="element-402">
                  {/* Header Badge */}
                  <div className="bg-brand-pink px-4 py-1.5 flex items-center justify-center gap-1.5" data-id="element-403">
                    <Star className="w-3.5 h-3.5 text-brand-dark fill-brand-dark" data-id="element-404" />
                    <span className="text-brand-dark font-bold text-xs uppercase tracking-wide" data-id="element-405">
                      Altamente Recomendado
                    </span>
                    <Star className="w-3.5 h-3.5 text-brand-dark fill-brand-dark" data-id="element-406" />
                  </div>

                  <div className="p-4" data-id="element-407">
                    <div className="flex items-start gap-3" data-id="element-408">
                      <div className="flex-shrink-0 mt-1" data-id="element-409">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${orderBump2 ? 'bg-brand-pink border-brand-pink' : 'border-gray-300'}`} data-id="element-410">
                          {orderBump2 && <Check className="w-3 h-3 text-white" data-id="element-411" />}
                        </div>
                      </div>
                      <div className="flex-1" data-id="element-412">
                        <div className="flex items-center gap-2 mb-1" data-id="element-413">
                          <img src="https://cdn.magicpatterns.com/uploads/6p6HfLMdonMcxW7yKUXFkB/order_bump_2.png" alt="Cobro + Entrega Pro" className="w-12 h-12 rounded-lg object-cover" data-id="element-414" />
                          <div data-id="element-415">
                            <h4 className="font-bold text-gray-900 text-sm" data-id="element-416">
                              Cobra y Entrega Como Marca (En 5 Min)
                            </h4>
                            <p className="text-brand-pink font-bold text-sm" data-id="element-417">
                              + {formatPrice(PRICES.orderBump)}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-600 text-xs leading-relaxed" data-id="element-418">
                          Agrega Cobro + Entrega Pro y recibe mensajes listos
                          para cobrar, entregar y dar seguimiento sin enredos.
                          Incluye checklist para que tu primera venta se sienta
                          profesional, ordenada y premium desde el celular.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center mb-6" data-id="element-419">
                  Descarga inmediata • Empieza hoy • Solo necesitas tu celular
                </p>

                <div className="flex items-center justify-center text-sm text-gray-500 bg-gray-50 py-3 rounded-lg" data-id="element-420">
                  <ShieldCheck className="w-5 h-5 text-emerald-500 mr-2" data-id="element-421" />
                  <span data-id="element-422">Garantía de satisfacción de 30 días</span>
                </div>

                {/* Value Stack Summary */}
                <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-423">
                  <div className="space-y-2 text-sm" data-id="element-424">
                    <div className="flex justify-between text-gray-600" data-id="element-425">
                      <span data-id="element-426">Mamá Digital 2.0</span>
                      <span className="line-through" data-id="element-427">
                        {formatPrice(PRICES.productOriginal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600" data-id="element-428">
                      <span data-id="element-429">3 Bonos de Lujo</span>
                      <span className="line-through" data-id="element-430">
                        {formatPrice(PRICES.bonuses)}
                      </span>
                    </div>
                    {orderBump1 && <div className="flex justify-between text-brand-pink" data-id="element-431">
                        <span data-id="element-432">Kit Copy-Paste</span>
                        <span data-id="element-433">{formatPrice(PRICES.orderBump)}</span>
                      </div>}
                    {orderBump2 && <div className="flex justify-between text-brand-pink" data-id="element-434">
                        <span data-id="element-435">Cobro + Entrega Pro</span>
                        <span data-id="element-436">{formatPrice(PRICES.orderBump)}</span>
                      </div>}
                    <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-100" data-id="element-437">
                      <span data-id="element-438">Valor Total</span>
                      <span className="line-through" data-id="element-439">
                        {formatPrice(PRICES.total)}
                      </span>
                    </div>
                    <div className="flex justify-between font-bold text-brand-pink text-lg" data-id="element-440">
                      <span data-id="element-441">Hoy pagas solo</span>
                      <span data-id="element-442">{formatPrice(totalPrice)}</span>
                    </div>
                  </div>

                  {/* Dynamic Checkout Button */}
                  <button onClick={handleCheckout} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-6 rounded-full text-base transition-all duration-200 transform hover:-translate-y-1 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 inline-flex items-center justify-center gap-2" data-id="element-443">
                    <Rocket className="w-5 h-5" data-id="element-444" />
                    ¡Lanza Tu Negocio En 7 Días! • {formatPrice(totalPrice)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>;
}